import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const ensureMetaTag = (selector, attributes) => {
  let tag = document.head.querySelector(selector);
  if (!tag) {
    tag = document.createElement("meta");
    Object.entries(attributes).forEach(([key, value]) => tag.setAttribute(key, value));
    document.head.appendChild(tag);
  }
  return tag;
};

export default function SEO({
  title,
  description,
  image = "/logo-color.svg",
  noindex = false,
  structuredData = null,
}) {
  const location = useLocation();

  useEffect(() => {
    const siteName = "JSY Printing & Packaging";
    const baseTitle = title ? `${title} | ${siteName}` : siteName;
    document.title = baseTitle;

    const origin = window.location.origin;
    const canonicalUrl = `${origin}${location.pathname}`;
    const imageUrl = image.startsWith("http") ? image : `${origin}${image}`;

    const metaDescription = ensureMetaTag('meta[name="description"]', { name: "description" });
    metaDescription.setAttribute("content", description || "Premium plastic packaging and printing solutions for the textile industry.");

    const metaRobots = ensureMetaTag('meta[name="robots"]', { name: "robots" });
    metaRobots.setAttribute("content", noindex ? "noindex, nofollow" : "index, follow");

    const ogTitle = ensureMetaTag('meta[property="og:title"]', { property: "og:title" });
    ogTitle.setAttribute("content", baseTitle);

    const ogDescription = ensureMetaTag('meta[property="og:description"]', { property: "og:description" });
    ogDescription.setAttribute("content", description || "Premium plastic packaging and printing solutions for the textile industry.");

    const ogType = ensureMetaTag('meta[property="og:type"]', { property: "og:type" });
    ogType.setAttribute("content", "website");

    const ogUrl = ensureMetaTag('meta[property="og:url"]', { property: "og:url" });
    ogUrl.setAttribute("content", canonicalUrl);

    const ogImage = ensureMetaTag('meta[property="og:image"]', { property: "og:image" });
    ogImage.setAttribute("content", imageUrl);

    const twitterCard = ensureMetaTag('meta[name="twitter:card"]', { name: "twitter:card" });
    twitterCard.setAttribute("content", "summary_large_image");

    const twitterTitle = ensureMetaTag('meta[name="twitter:title"]', { name: "twitter:title" });
    twitterTitle.setAttribute("content", baseTitle);

    const twitterDescription = ensureMetaTag('meta[name="twitter:description"]', { name: "twitter:description" });
    twitterDescription.setAttribute("content", description || "Premium plastic packaging and printing solutions for the textile industry.");

    const twitterImage = ensureMetaTag('meta[name="twitter:image"]', { name: "twitter:image" });
    twitterImage.setAttribute("content", imageUrl);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const existingLd = document.head.querySelector('script[data-seo="ld-json"]');
    if (existingLd) existingLd.remove();

    if (structuredData) {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-seo", "ld-json");
      script.text = JSON.stringify(structuredData);
      document.head.appendChild(script);
    }
  }, [description, image, location.pathname, noindex, structuredData, title]);

  return null;
}
